import { useEffect } from "react"
import { Value } from "@convertkit/slate"
import { defaultSlateValue } from "../blocks/text/default-slate-value"

function convertToImmutable(value) {
  return Value.fromJSON(value || defaultSlateValue)
}

export function isImmutable(value) {
  return Boolean(value?._map)
}

/**
 * The Slate editor requires an immutable object to be passed to its `value`
 * prop. However, there are cases where we're storing this object as a JS
 * object, so we need to convert it before passing it as a prop.
 *
 * This conversion is an expensive process, so we only want to do this when
 * necessary. useSlateValue will watch for when this is needed and update the
 * state accordingly.
 *
 * It will always return an immutable object.
 *
 * Example usage:
 *
 * ```js
 * const { properties, update } = useBlocks(block_id)
 *
 * const value = useSlateValue(properties.value, update)
 * ```
 *
 * @param {Object} value
 * @param {Function} update
 * @returns {Object} - an immutable object
 */
export function useSlateValue(value, update) {
  useEffect(() => {
    if (isImmutable(value)) return

    update({ value: convertToImmutable(value) })
  }, [value, update])

  if (!isImmutable(value)) {
    return convertToImmutable(value)
  }

  return value
}
